import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useWebRTC } from '../context/WebRTCContext';
import { useKeywords } from '../context/KeywordContext';
import { confirmDialog } from './ui/dialog';
import { logAction, confirmAction, failAction } from './ActionAuditLog';
import './WindowActionPanel.css';

/**
 * WindowActionPanel - Floating action panel that attaches to the selected window
 * Dynamically positions left/right based on window proximity to screen edge
 * Uses green styling to match the targeted window outline
 */
const WindowActionPanel = ({ selectedWindow, videoContainerRef, nativeResolution }) => {
  const { featureManager } = useWebRTC();
  const { checkKeywords } = useKeywords();
  const panelRef = useRef(null);
  const prevSideRef = useRef(null);

  const [actionLoading, setActionLoading] = useState(null);
  const [isPinned, setIsPinned] = useState(false);
  const [panelPosition, setPanelPosition] = useState(null);
  const [isSwappingSides, setIsSwappingSides] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [enforcementState, setEnforcementState] = useState({ activeOverlays: [] });

  // Watch for video to be ready (has dimensions)
  useEffect(() => {
    if (!videoContainerRef?.current) return;

    const checkVideo = () => {
      const video = videoContainerRef.current?.querySelector('video');
      if (video && video.videoWidth > 0) {
        setVideoReady(true);
      }
    };

    checkVideo();
    const interval = setInterval(checkVideo, 100);

    return () => clearInterval(interval);
  }, [videoContainerRef]);

  // Keep the always-on-top toggle in sync with the live window state,
  // which arrives via position updates (windowInfo.isAlwaysOnTop).
  useEffect(() => {
    setIsPinned(!!selectedWindow?.isAlwaysOnTop);
  }, [selectedWindow?.hwnd, selectedWindow?.isAlwaysOnTop]);

  // Check if window title matches any flagged keywords
  const flagResult = useMemo(() => {
    if (!selectedWindow?.title) return null;
    const result = checkKeywords(selectedWindow.title);
    return result.matched ? result : null;
  }, [selectedWindow?.title, checkKeywords]);

  const [appUsage, setAppUsage] = useState(null);
  const [existingLimit, setExistingLimit] = useState(null);

  // Fade in once position is calculated
  useEffect(() => {
    if (selectedWindow && panelPosition) {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [selectedWindow, panelPosition]);

  // Get time-limit feature for checking existing limits
  useEffect(() => {
    if (!selectedWindow || !featureManager) return;

    const timeLimit = featureManager.getFeature('time-limit');
    if (timeLimit) {
      const appName = selectedWindow.processName || selectedWindow.owner;
      if (appName) {
        const limit = timeLimit.getAppLimit(appName);
        setExistingLimit(limit);

        const usage = timeLimit.getAppUsage(appName);
        setAppUsage(usage);
      }
    }
  }, [selectedWindow, featureManager]);

  // Subscribe to enforcement state updates
  useEffect(() => {
    if (!featureManager) return;

    const timeLimit = featureManager.getFeature('time-limit');
    if (!timeLimit) return;

    // Get initial state
    setEnforcementState(timeLimit.getEnforcementStateData() || { activeOverlays: [] });

    // Subscribe to updates
    const unsubscribe = timeLimit.onEnforcementStateUpdate((state) => {
      setEnforcementState(state || { activeOverlays: [] });
    });

    return () => unsubscribe();
  }, [featureManager]);

  // Calculate panel position based on window location
  // Uses the same coordinate conversion as window-control feature
  useEffect(() => {
    if (!selectedWindow || !videoContainerRef?.current) return;

    const container = videoContainerRef.current;
    const video = container.querySelector('video');
    if (!video || !video.videoWidth) return;

    // Get display dimensions (CSS rendered size) - same as window-control
    const displayWidth = video.clientWidth;
    const displayHeight = video.clientHeight;

    // Use native screen resolution if available, fall back to video intrinsic dimensions
    // selectedWindow should have screenWidth/screenHeight from desktop
    const sourceWidth = selectedWindow.screenWidth || nativeResolution?.width || video.videoWidth;
    const sourceHeight = selectedWindow.screenHeight || nativeResolution?.height || video.videoHeight;

    // Calculate scale factors from native screen to display
    const scaleX = displayWidth / sourceWidth;
    const scaleY = displayHeight / sourceHeight;

    // Convert window coordinates to display coordinates
    const windowDisplayX = selectedWindow.x * scaleX;
    const windowDisplayY = selectedWindow.y * scaleY;
    const windowDisplayWidth = selectedWindow.width * scaleX;

    const panelWidth = 152;
    const gap = 6; // Gap between window edge and panel

    // Check available space on each side of the window
    const spaceOnRight = displayWidth - (windowDisplayX + windowDisplayWidth);
    const spaceOnLeft = windowDisplayX;

    let side, left;

    // Position panel on the side with more space, directly adjacent to the window
    if (spaceOnRight >= panelWidth + gap) {
      // Attach to right edge of window
      side = 'right';
      left = windowDisplayX + windowDisplayWidth + gap;
    } else if (spaceOnLeft >= panelWidth + gap) {
      // Attach to left edge of window
      side = 'left';
      left = windowDisplayX - panelWidth - gap;
    } else {
      // Not enough space on either side, position based on which side window is on
      const windowCenterX = windowDisplayX + windowDisplayWidth / 2;
      const videoCenterX = displayWidth / 2;
      side = windowCenterX < videoCenterX ? 'right' : 'left';

      if (side === 'right') {
        left = windowDisplayX + windowDisplayWidth + gap;
      } else {
        left = windowDisplayX - panelWidth - gap;
      }
    }

    // Vertical position - align with top of window, keep within display bounds
    let top = windowDisplayY;
    const minTop = 8;
    const maxTop = displayHeight - 260; // Panel height ~250px
    top = Math.max(minTop, Math.min(top, Math.max(minTop, maxTop)));

    // Clamp left position to stay within display
    const minLeft = 8;
    const maxLeft = displayWidth - panelWidth - 8;
    left = Math.max(minLeft, Math.min(left, maxLeft));

    // Detect side swap and trigger animation
    if (prevSideRef.current && prevSideRef.current !== side) {
      setIsSwappingSides(true);
      setTimeout(() => setIsSwappingSides(false), 300);
    }
    prevSideRef.current = side;

    setPanelPosition({ side, top, left });
  }, [selectedWindow, videoContainerRef, nativeResolution, videoReady]);

  // Execute window control action
  const executeAction = useCallback((actionName, payload = {}) => {
    if (!selectedWindow || !featureManager) return;

    const windowControl = featureManager.getFeature('window-control');
    if (!windowControl) return;

    setActionLoading(actionName);
    const actId = logAction({
      label: actionName === 'close_window' ? 'Close Window' : actionName,
      icon: actionName === 'close_window' ? '✖' : '🪟',
      featureId: 'window-control',
      messageType: actionName,
    });
    try {
      windowControl.sendMessage(actionName, {
        hwnd: selectedWindow.hwnd,
        ...payload
      });
      confirmAction(actId);
    } catch (err) {
      console.error('Window action error:', err);
      failAction(actId, err.message);
    }

    setTimeout(() => setActionLoading(null), 500);
  }, [selectedWindow, featureManager]);

  // Toggle always-on-top for the selected window
  const handleToggleAlwaysOnTop = useCallback(() => {
    if (!selectedWindow) return;
    const next = !isPinned;
    setIsPinned(next); // optimistic; live state re-syncs from position updates
    executeAction('set_always_on_top', { enabled: next });
  }, [selectedWindow, isPinned, executeAction]);

  // Check if the selected window's app is currently restricted
  const isAppRestricted = useMemo(() => {
    if (!selectedWindow || !enforcementState?.activeOverlays) return false;

    const appName = selectedWindow.processName || selectedWindow.owner;
    if (!appName) return false;

    return enforcementState.activeOverlays.some(
      overlay => overlay.appName?.toLowerCase() === appName.toLowerCase() && overlay.isRemoteBlocked
    );
  }, [selectedWindow, enforcementState]);

  // Quick block using time-limit feature
  const handleQuickBlock = useCallback(async () => {
    if (!selectedWindow || !featureManager) return;

    const timeLimit = featureManager.getFeature('time-limit');
    if (!timeLimit) return;

    setActionLoading('block');
    const actId = logAction({ label: 'Restrict Window', icon: '🚫', featureId: 'time-limit', messageType: 'remoteBlockWindow' });

    try {
      await timeLimit.remoteBlockWindow({
        hwnd: selectedWindow.hwnd,
        x: selectedWindow.x + selectedWindow.width / 2,
        y: selectedWindow.y + selectedWindow.height / 2,
        reason: 'Remote block'
      });
      confirmAction(actId);
    } catch (err) {
      console.error('Block error:', err);
      failAction(actId, err.message);
    }

    setActionLoading(null);
  }, [selectedWindow, featureManager]);

  // Quick unblock using time-limit feature
  const handleQuickUnblock = useCallback(async () => {
    if (!selectedWindow || !featureManager) return;

    const timeLimit = featureManager.getFeature('time-limit');
    if (!timeLimit) return;

    const appName = selectedWindow.processName || selectedWindow.owner;
    if (!appName) return;

    setActionLoading('unblock');
    const actId = logAction({ label: 'Unrestrict Window', icon: '✅', featureId: 'time-limit', messageType: 'remoteUnblockWindow' });

    try {
      await timeLimit.remoteUnblockWindow(appName);
      confirmAction(actId);
    } catch (err) {
      console.error('Unblock error:', err);
      failAction(actId, err.message);
    }

    setActionLoading(null);
  }, [selectedWindow, featureManager]);

  if (!selectedWindow) return null;

  const appName = selectedWindow.processName || selectedWindow.owner || 'Unknown';

  // Build style object
  const panelStyle = panelPosition
    ? { top: `${panelPosition.top}px`, left: `${panelPosition.left}px` }
    : {};

  const panelClasses = [
    'window-action-panel',
    panelPosition?.side || 'right',
    !panelPosition && 'fallback-position',
    flagResult && 'flagged',
    isVisible && 'visible',
    isSwappingSides && 'swapping'
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={panelRef}
      className={panelClasses}
      style={panelStyle}
    >
      {/* Connector line to window */}
      <div className="panel-connector">
        <div className="connector-line" />
        <div className="connector-dot" />
      </div>

      {/* Panel content */}
      <div className="panel-inner">
        {/* Header with window info */}
        <div className="panel-header">
          {flagResult && (
            <div className="flag-indicator">
              <span className="flag-emoji">&#9888;</span>
              <div className="flag-tags">
                {flagResult.groups.map(g => (
                  <span
                    key={g.id}
                    className="flag-tag"
                    style={{ backgroundColor: g.color }}
                  >
                    {g.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="window-info">
            <span className="app-badge">{appName}</span>
            <span className="window-title" data-tooltip={selectedWindow.title}>
              {selectedWindow.title?.substring(0, 28) || 'Untitled'}
              {selectedWindow.title?.length > 28 ? '...' : ''}
            </span>
          </div>

          {appUsage > 0 && (
            <div className="usage-row">
              <span className="usage-label">Today</span>
              <span className="usage-value">{formatTime(appUsage)}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="panel-actions">
          {isAppRestricted ? (
            <button
              className="action-btn unrestrict"
              onClick={handleQuickUnblock}
              disabled={actionLoading === 'unblock'}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="9 12 12 15 16 10"/>
              </svg>
              <span>Unrestrict</span>
            </button>
          ) : (
            <button
              className="action-btn restrict"
              onClick={handleQuickBlock}
              disabled={actionLoading === 'block'}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
              </svg>
              <span>Restrict</span>
            </button>
          )}

          <button
            className={`action-btn pin${isPinned ? ' active' : ''}`}
            onClick={handleToggleAlwaysOnTop}
            disabled={actionLoading === 'set_always_on_top'}
            title={isPinned ? 'Unpin from top' : 'Keep this window always on top'}
          >
            <svg viewBox="0 0 24 24" fill={isPinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
              <path d="M12 17v5"/>
              <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z"/>
            </svg>
            <span>{isPinned ? 'Pinned' : 'Always on Top'}</span>
          </button>

          <button
            className="action-btn timeline"
            onClick={() => {/* TODO: Navigate to timeline view */}}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            <span>Timeline</span>
          </button>

          <button
            className="action-btn close"
            onClick={async () => {
              if (await confirmDialog({ title: 'Close Window', message: `Close "${selectedWindow.title}"?`, confirmLabel: 'Close', danger: true })) {
                executeAction('close_window');
              }
            }}
            disabled={actionLoading === 'close_window'}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
            <span>Close</span>
          </button>

          {existingLimit && (
            <div className="limit-badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              <span>{formatTime(existingLimit.daily_limit_seconds)} limit</span>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

function formatTime(seconds) {
  if (seconds < 0) seconds = 0;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

export default WindowActionPanel;
